import { Group, SegmentedControl, Text, Title } from '@mantine/core';
import { useRequestPaymentPlan } from 'api';
import type {
  PaymentPlanComplete,
  PaymentPlansListResponse,
  RequestPaymentPlanBody,
} from 'api/models';
import classNames from 'classnames/bind';
import { Logo } from 'components/Logo/Logo';
import { jotaiStore } from 'init';
import { currentLanguageAtom, i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useReducer, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { isEmpty } from 'remeda';
import { Icon, IconName } from 'shared/components/Icon';
import Tabs from 'shared/components/Tabs';
import { Button, ButtonColor } from 'shared/io';
import { error, success } from 'shared/notifications';
import apiService from 'shared/service/api.service';
import { formatAmount } from 'shared/utils/currency';
import { isProduction } from 'shared/utils/environment';
import { setQueryParams } from 'shared/utils/url';
import { reducerState, removeAttributes } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import { APPLICATION_LANGUAGES, type ApplicationLocale, ApplicationLocaleSchema } from 'types';
import type { CurrencyCode } from 'types/currency';
import type { DebtorReactionInvoice } from 'types/debtor';
import type { MetaData } from 'types/storeTypes';
import DebtorReactionCardHeader from './DebtorReactionCardHeader';
import DebtorReactionInvoicesList from './DebtorReactionInvoicesList';
import DebtorReactionPaymentPlansList from './DebtorReactionPaymentPlansList';
import styleIdentifiers from './debtorReaction.module.scss';
import RequestPaymentPlanModal from './RequestPaymentPlanModal/RequestPaymentPlanModal';

type TabIndex = 0 | 1 | 2 | 3;

const styles = classNames.bind(styleIdentifiers);

// MARK: State definition
type State = {
  company_data?: {
    country_code: string;
    has_logo: boolean;
    has_cgv: boolean;
    iban: string;
    company_name: string;
    vat_number: string;
    use_digiteal_integration: boolean;
    has_trademark: boolean;
    can_use_online_payment: boolean;
    can_use_payment_plans: boolean;
    can_request_payment_plans: boolean;
  };
  debtor_data: { full_name: string; currency: CurrencyCode };
  totalRemainingBalance: number;
  invoices: {
    data: DebtorReactionInvoice[];
    metadata: MetaData;
    currentPage: 1;
  };
  paidInvoices?: {
    data: DebtorReactionInvoice[];
    metadata: MetaData;
    currentPage: 1;
  };
  payments_sum: number;
  credit_notes: DebtorReactionInvoice[];
  creditNotes?: {
    data: DebtorReactionInvoice[];
    metadata: MetaData;
  };
  payment_plans?: PaymentPlansListResponse;
};

// MARK: Component
export default function DebtorReaction() {
  const { t, currentLang } = useTranslation();
  const [logoUrl, setLogoUrl] = useState<string>('');
  const location = useLocation();
  const { params } = useRouteMatch<{ id: any }>();

  const [queryStrings] = useState(qs.parse(location.search));

  const [data, setData] = useReducer<State, any>(reducerState, null as any);
  const [selectedInvoices, setSelectedInvoices] = useState<DebtorReactionInvoice[]>([]);
  const [isAllSelected, setSelectAll] = useState(false);
  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const form = useForm();
  const { mutate: requestPaymentPlan } = useRequestPaymentPlan();

  const amountToPay = selectedInvoices.reduce<number | null>((acc, invoice) => {
    const amountToAdd =
      (invoice as any).id === 'payment'
        ? data.payments_sum
        : invoice.type === 'debtor_reaction_credit_note'
          ? +invoice.localized_money_object.remaining_balance
          : +invoice.localized_money_object.remaining_balance_with_fees;

    if (amountToAdd === 0) return acc;
    return (acc ?? 0) + amountToAdd;
  }, null);

  const isInvoicesTabActive = activeTab === 1;

  const paymentLink = ({
    amount,
    communicated_references,
    structured_communication,
  }: {
    amount: number;
    communicated_references?: string;
    structured_communication?: string;
  }) => {
    const fullPayment = Math.round(amount * 100);

    // todo: Is this used in demo? Does it need tailored values for that env?
    window.location.href = `https://${
      isProduction() ? 'api' : 'test'
    }.digiteal.eu/api/v1/payment-request/pay-button/execute?requesterVAT=${
      isProduction() ? company_data.vat_number : 'BE0206607129'
    }${structured_communication ? `&creditorReference=${structured_communication}` : ''}${
      communicated_references ? `&remittanceInfo=${communicated_references}` : ''
    }&amountInCents=${fullPayment}&iban=${
      isProduction() ? company_data.iban : 'BE95330012367958'
    }&language=${currentLang.toUpperCase()}&confirmationURL=${encodeURIComponent(
      `${window.location.href}&paymentSucceeded=true`,
    )}&errorURL=${encodeURIComponent(
      `${window.location.href}&paymentError=true`,
    )}&cancelURL=${encodeURIComponent(window.location.href)}`;
  };

  // #region Huge effect
  useEffect(() => {
    const parsedLocale = ApplicationLocaleSchema.safeParse(queryStrings.locale);
    if (parsedLocale.success) jotaiStore.set(currentLanguageAtom, parsedLocale.data);
  }, [queryStrings.locale]);

  useEffect(() => {
    if (queryStrings.paymentError) {
      error({
        text: i18nKeys.DEBTOR.REACTION.PAYMENT_FAILED,
      });
      setQueryParams('paymentError', undefined);
    }

    if (queryStrings.paymentSucceeded) {
      success({
        text: i18nKeys.DEBTOR.REACTION.PAYMENT_SUCCEEDED,
      });
      setQueryParams('paymentSucceeded', undefined);
    }

    invoiceActions.debtorReaction({
      id: params.id,
      data: {
        token: queryStrings.document_token,
      },
      callback: (debtorReactionData: any) => {
        debtorReactionData.invoices.data = debtorReactionData.invoices.data.map((invoice: any) =>
          removeAttributes(invoice),
        );

        debtorReactionData.credit_notes = debtorReactionData.credit_notes.map((cn: any) =>
          removeAttributes(cn),
        );

        debtorReactionData.payments_sum = -debtorReactionData.payments_sum;

        if (debtorReactionData.company_data.has_logo) {
          invoiceActions.document({
            id: params.id,
            public: true,
            noFeedBack: true,
            noLoading: true,
            fileUpload: true,
            document_type: 'company_logo',
            data: {
              token: queryStrings.document_token,
            },
            callback: (logo: Blob) => {
              if (logo) setLogoUrl(URL.createObjectURL(logo));
            },
          });
        }

        setData({
          ...debtorReactionData,
          totalRemainingBalance:
            debtorReactionData.invoices.data.reduce(
              (acc: any, invoice: any) =>
                acc + Number(invoice.localized_money_object.remaining_balance_with_fees),
              0,
            ) +
            debtorReactionData.credit_notes.reduce(
              (acc: any, cn: any) => acc + Number(cn.localized_money_object.remaining_balance),
              0,
            ) +
            debtorReactionData.payments_sum,
        });
      },
    });

    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, []);
  // #endregion Huge effect

  // MARK: Early return
  if (data == null || data.company_data == null) return null;

  const { company_data } = data;

  // Here they've added a debtor data into a company object : company_data.can_request_payment_plans
  const shouldDisplayPaymentPlanTab =
    (company_data.can_request_payment_plans && company_data.can_use_payment_plans) ||
    !isEmpty(data?.payment_plans?.data ?? []);
  const isFrenchClient = company_data.country_code === 'FR';
  const hasPaymentOptions = isFrenchClient ? company_data.use_digiteal_integration : true;

  const invoices = isInvoicesTabActive
    ? (data?.paidInvoices?.data ?? [])
    : (data?.invoices?.data ?? []);

  // #region Callbacks

  const downloadCgv = () => {
    invoiceActions.document({
      id: params.id,
      public: true,
      document_type: 'payment_terms_pdf',
      data: {
        token: queryStrings.document_token,
      },
    });
  };

  const handleSelectAll = (active: boolean) => {
    const newFormValues: Record<string, any> = {
      payment: active,
    };

    setSelectedInvoices(
      active
        ? [
            ...(invoices || []).slice(),
            ...(data.credit_notes || []).slice(),
            {
              id: 'payment',
              remaining_balance_with_fees: data.payments_sum,
            } as any,
          ]
        : [],
    );

    for (const invoice of invoices || []) {
      newFormValues[invoice.id] = active;
    }

    for (const cn of data?.credit_notes || []) {
      newFormValues[cn.id] = active;
    }

    form.reset(newFormValues);
    setSelectAll(active);
  };

  const onCheckboxChange = (invoice: DebtorReactionInvoice) => (checked: any) => {
    const newSelectedInvoices = selectedInvoices.slice();
    if (checked) {
      newSelectedInvoices.push(invoice);
    } else {
      newSelectedInvoices.splice(
        newSelectedInvoices.findIndex(selectedInvoice => selectedInvoice.id === invoice.id),
        1,
      );
    }

    if (
      newSelectedInvoices.length ===
      invoices.length + data.credit_notes.length + (data.payments_sum !== 0 ? 1 : 0)
    ) {
      if (!isAllSelected) setSelectAll(true);
    } else if (isAllSelected) setSelectAll(false);

    setSelectedInvoices(newSelectedInvoices);
  };

  const showQrCode = (invoicesSelected: Array<{ id: unknown }>) => {
    invoiceActions.debtorReactionQrCode({
      id: params.id,
      data: {
        invoice_ids: invoicesSelected.map(invoice => invoice.id),
        amount: amountToPay,
        token: queryStrings.document_token,
      },
      callback: ({ data: response }: any) => {
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.MEDIUM,
          title: t(i18nKeys.DEBTOR.REACTION.PAYMENT_QR_CODE),
          children: (
            <div className={styles('qr-code-modal')}>
              <div>{t(i18nKeys.DEBTOR.REACTION.QR_CODE_DESCRIPTION)}</div>
              <img src={response} alt="qr code" />
            </div>
          ),
        });
      },
    });
  };

  const pay = (
    invoicesToPay: Array<{ id: number; structured_communication: string; reference: string }>,
  ) => {
    if (!company_data.use_digiteal_integration) {
      showQrCode(invoicesToPay);
    } else if (isAllSelected ? data.invoices.data.length === 1 : invoicesToPay.length === 1) {
      paymentLink({
        amount: amountToPay ?? 0,
        structured_communication: invoicesToPay[0].structured_communication,
      });
    } else {
      invoiceActions.getGroupedInvoicePaymentInfo({
        id: params.id,
        data: {
          invoice_ids: invoicesToPay.map(invoice => invoice.id),
          token: queryStrings.document_token,
        },
        callback: ({ data: response }: any) => {
          paymentLink({
            amount: amountToPay ?? 0,
            communicated_references: response.attributes.communicated_invoices,
          });
        },
      });
    }
  };

  const onChangeSelectAll = () => {
    handleSelectAll(!isAllSelected);
  };

  const onChangeTab = (value: TabIndex, oldTabIndex: TabIndex) => {
    setActiveTab(value as TabIndex);
    if (oldTabIndex === 0) {
      handleSelectAll(false);
    }

    if (value === 1) {
      if (!data?.paidInvoices) {
        invoiceActions.debtorReactionInvoices({
          id: params.id,
          data: {
            page: 1,
            token: queryStrings.document_token,
          },
          callback: (paidInvoices: any) => {
            paidInvoices.data = paidInvoices.data.map((elements: any) =>
              removeAttributes(elements),
            );
            setData({
              ...data!,
              paidInvoices,
            });
          },
        });
      }
    } else if (value === 2) {
      if (!data?.creditNotes) {
        invoiceActions.debtorReactionCreditNotes({
          id: params.id,
          data: {
            page: 1,
            token: queryStrings.document_token,
          },
          callback: (creditNotes: any) => {
            creditNotes.data = creditNotes.data.map((elements: any) => removeAttributes(elements));
            setData({
              ...data!,
              creditNotes,
            });
          },
        });
      }
    } else if (value === 3) {
      if (!data?.payment_plans) {
        setData({
          ...data!,
        });
      }
    }
  };

  const handleRequestPaymentPlan = (planInvoices: any, amount: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.DEBTOR.REACTION.REQUEST_PAYMENT_PLAN),
      children: (
        <RequestPaymentPlanModal
          invoices={planInvoices}
          invoiceId={params.id}
          token={queryStrings.document_token as string}
          amount={amount}
          currency={data.debtor_data.currency}
          onSubmit={(values: RequestPaymentPlanBody) => {
            requestPaymentPlan(
              {
                data: values,
              },
              {
                onSuccess: () => {
                  dialogHide(DialogShowId.CUSTOM);
                },
              },
            );
          }}
        />
      ),
    });
  };
  // #endregion Callbacks

  const isAllEligibleForPaymentPlan = selectedInvoices.every(
    invoice => invoice.eligible_for_payment_plan,
  );

  // MARK: Return
  return (
    <div className={styles('DebtorReaction')}>
      <div className={styles('layout-container')}>
        <div className={styles('head')}>
          <div>
            {logoUrl && <img src={logoUrl} alt="" />}
            <div>
              {data?.debtor_data && (
                <Title order={4}>
                  {t(i18nKeys.DEBTOR.REACTION.CLIENT_PORTAL_OF, {
                    text: data!.debtor_data!.full_name,
                  })}
                </Title>
              )}
              <Title order={5} c="gray.6" className={styles('debtor-fullname')}>
                {t(i18nKeys.INVOICE.YOUR_INVOICE_FOR)} {company_data.company_name}
              </Title>
            </div>
          </div>

          <div className={styles('lang-container')}>
            <SegmentedControl
              value={currentLang}
              onChange={val => jotaiStore.set(currentLanguageAtom, val as ApplicationLocale)}
              data={APPLICATION_LANGUAGES.map(lang => ({
                label: lang.value.toUpperCase(),
                value: lang.value,
              }))}
              color="purple.6"
              styles={theme => ({
                root: {
                  borderRadius: '8px',
                  border: `1px solid ${theme.colors.gray[3]}`,
                },
              })}
            />
            {company_data.has_cgv && (
              <div className={styles('cgv')} onClick={downloadCgv}>
                <Icon name={IconName.DOWNLOAD} /> {t(i18nKeys.DEBTOR.REACTION.GTC)}
              </div>
            )}
          </div>
        </div>
        <FormProvider {...form}>
          <Tabs
            className={styles('tabs')}
            tabs={[
              t(i18nKeys.STATEMENT),
              t(i18nKeys.INVOICES),
              t(i18nKeys.NAV.CREDIT_NOTES),
              ...(shouldDisplayPaymentPlanTab ? [t(i18nKeys.NAV.PAYMENT_PLAN)] : []),
            ]}
            tabIndex={activeTab}
            onChange={(newIdx: number, oldIdx: number) =>
              onChangeTab(newIdx as TabIndex, oldIdx as TabIndex)
            }
          >
            {/*
            // #region Tab 1
            */}
            <div className={styles('card-content')}>
              {company_data.can_use_online_payment && (
                <DebtorReactionCardHeader
                  data={data}
                  hasPaymentOptions={hasPaymentOptions}
                  selectAll={isAllSelected}
                  onChangeSelectAll={onChangeSelectAll}
                />
              )}
              <DebtorReactionInvoicesList
                billingStatement
                data={data}
                items={invoices}
                metadata={data?.invoices?.metadata}
                isInvoicesTabActive={isInvoicesTabActive}
                onCheckboxChange={onCheckboxChange}
                setData={setData}
                paymentLink={pay}
                queryStrings={queryStrings}
                canUseOnlinePayment={company_data.can_use_online_payment}
                canUsePaymentPlan={company_data.can_use_payment_plans}
                canRequestPaymentPlan={company_data.can_request_payment_plans}
                hasPaymentOptions={hasPaymentOptions}
                isFrenchClient={isFrenchClient}
                canUseDigiteal={company_data.use_digiteal_integration}
              />
              <div className={styles('pay-button-container')}>
                {!isInvoicesTabActive && (amountToPay ?? 0) > 0 && (
                  <>
                    {company_data.can_use_payment_plans &&
                      company_data.can_request_payment_plans && (
                        <Button
                          label={t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)}
                          onClick={() => handleRequestPaymentPlan(selectedInvoices, amountToPay)}
                          iconLeft={IconName.PAYMENT_BOOK}
                          iconSize="20px"
                          disabled={!isAllEligibleForPaymentPlan}
                        />
                      )}
                    {company_data.can_use_online_payment &&
                      (isFrenchClient ? company_data.use_digiteal_integration : true) && (
                        <Button
                          onClick={() => pay(selectedInvoices)}
                          color={ButtonColor.MAIN}
                          iconLeft={
                            company_data.use_digiteal_integration
                              ? IconName.CREDIT_CARD
                              : IconName.QR_CODE
                          }
                          iconSize="20px"
                        >
                          <span>
                            {t(i18nKeys.DEBTOR.REACTION.PAY)}
                            {' : '}
                            {formatAmount(Math.max(0, amountToPay ?? 0))}
                          </span>
                        </Button>
                      )}
                  </>
                )}
              </div>
            </div>
            {/*
            // #endregion Tab 1
            */}
            {/*
            // #region Tab 2
            */}
            <div className={styles('card-content')}>
              <DebtorReactionInvoicesList
                data={data}
                items={data?.paidInvoices?.data || []}
                isInvoicesTabActive={isInvoicesTabActive}
                onCheckboxChange={onCheckboxChange}
                metadata={data?.paidInvoices?.metadata}
                setData={setData}
                paymentLink={pay}
                queryStrings={queryStrings}
                canUseOnlinePayment={company_data.can_use_online_payment}
                isFrenchClient={isFrenchClient}
                canUseDigiteal={company_data.use_digiteal_integration}
              />
            </div>
            {/*
            // #endregion Tab 2
            */}
            {/*
            // #region Tab 3
            */}
            <div className={styles('card-content')}>
              <DebtorReactionInvoicesList
                data={data}
                credit
                items={data?.creditNotes?.data || []}
                metadata={data?.creditNotes?.metadata}
                isInvoicesTabActive={isInvoicesTabActive}
                onCheckboxChange={onCheckboxChange}
                setData={setData}
                paymentLink={pay}
                queryStrings={queryStrings}
                canUseOnlinePayment={company_data.can_use_online_payment}
              />
            </div>
            {/*
            // #endregion Tab 3
            */}
            {/*
            // #region Tab 4
            */}
            {shouldDisplayPaymentPlanTab && (
              <div className={styles('card-content')}>
                <DebtorReactionPaymentPlansList
                  data={data}
                  items={(data?.payment_plans?.data || []) as PaymentPlanComplete[]}
                />
              </div>
            )}
            {/*
            // #endregion Tab 4
            */}
          </Tabs>
        </FormProvider>
        {company_data.has_trademark && (
          <a
            style={{ marginTop: '10px' }}
            href={`https://www.recovr.eu/?utm_source=client_referrer&utm_medium=email&utm_campaign=debtor_reaction&utm_content=${apiService.tenant}`}
            target="_blank"
            rel="noreferrer"
          >
            <Group w="100%" gap={7}>
              <Text>Powered by</Text>
              <Logo variant="fullLogo" height="30px" />
            </Group>
          </a>
        )}
      </div>
    </div>
  );
}
